/**
 * @fileoverview チャット入力コンポーネント
 * @description メッセージ入力フォームとディスクレイマー表示
 */
import { useState, useRef, useCallback, type KeyboardEvent } from 'react'
import { Send, Info } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * ChatInputのprops型
 */
interface ChatInputProps {
  /** メッセージ送信時のコールバック */
  onSend: (message: string) => void
  /** 送信中かどうか */
  isLoading?: boolean
  /** プレースホルダーテキスト */
  placeholder?: string
  /** ディスクレイマーを表示するか */
  showDisclaimer?: boolean
  /** カスタムクラス名 */
  className?: string
}

/**
 * チャット入力コンポーネント
 * @description メッセージ入力フォームとディスクレイマー
 * @example
 * <ChatInput onSend={handleSend} isLoading={isLoading} />
 */
export function ChatInput({
  onSend,
  isLoading = false,
  placeholder = '質問を入力...',
  showDisclaimer = true,
  className,
}: ChatInputProps) {
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  /**
   * メッセージ送信処理
   */
  const handleSend = useCallback(() => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage || isLoading) return

    onSend(trimmedMessage)
    setMessage('')

    // テキストエリアの高さをリセット
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [message, isLoading, onSend])

  /**
   * キーボードイベント処理
   * @description Enter（Shift無し）で送信、Shift+Enterで改行
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  /**
   * テキストエリアの高さを自動調整
   */
  const handleInput = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    // 高さをリセットしてから再計算
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
  }, [])

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* ディスクレイマー */}
      {showDisclaimer && (
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#9C9B99]">
          <Info className="h-3.5 w-3.5" />
          <span>AIの回答は参考情報です。最終判断はご自身で行ってください</span>
        </div>
      )}

      {/* 入力エリア */}
      <div className="flex items-end gap-3 rounded-3xl bg-[#F5F4F1] px-4 py-3">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={placeholder}
          disabled={isLoading}
          rows={1}
          className={cn(
            'flex-1 resize-none bg-transparent text-sm text-[#1A1918]',
            'placeholder:text-[#9C9B99]',
            'focus:outline-none',
            'disabled:opacity-50'
          )}
        />

        {/* 送信ボタン */}
        <button
          onClick={handleSend}
          disabled={!message.trim() || isLoading}
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
            'bg-[#3D8A5A] text-white',
            'transition-opacity',
            'disabled:opacity-50',
            'hover:bg-[#2E7D32]'
          )}
          aria-label="送信"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
